import { RefreshCw, Play } from 'lucide-react'
import React, { useCallback } from 'react'
import { NodeTextarea, CopyableContent, StatusIndicator } from '@/components/common'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import type {
  NodeJavascripterData,
  ExecutionState,
  CodeInputState,
  NodeDataSettings
} from '../types'
import styles from './JavascriptContent.module.css'

interface JavascriptContentProps {
  data: NodeJavascripterData
  codeInputState: CodeInputState
  executionState: ExecutionState
  dataSettings: NodeDataSettings
  linkedKnowledge?: string
  showDebugInfo: boolean
  isLocked: boolean
  onCodeChange: (value: string) => void
  onRequirementsChange: (value: string) => void
  onContextChange: (value: string) => void
  onCommandChange: (value: string) => void
  onCodeBlur: () => void
  onRequirementsBlur: () => void
  onContextBlur: () => void
  onStartProcessing: () => void
  onExecuteJavaScript: () => void
  onAcceptResult: () => void
  onResetProcessing: () => void
  nodeId: string
}

/**
 * Main content component for the Javascript Node.
 * Handles the code editor, requirements, and execution results.
 */
export const JavascriptContent: React.FC<JavascriptContentProps> = ({
  data,
  codeInputState,
  executionState,
  dataSettings,
  linkedKnowledge,
  showDebugInfo,
  isLocked,
  onCodeChange,
  onRequirementsChange,
  onContextChange,
  onCommandChange,
  onCodeBlur,
  onRequirementsBlur,
  onContextBlur,
  onStartProcessing,
  onExecuteJavaScript,
  onAcceptResult,
  onResetProcessing,
  nodeId
}) => {
  const { codeInput, requirementsInput, contextInput, command } = codeInputState
  const { 
    isProcessing, 
    statusMessage, 
    hasResult, 
    hasError, 
    hasExecutionResult, 
    isInProgress 
  } = executionState

  const formatExecutionTime = useCallback((time: number) => {
    return time < 1000 ? `${time}ms` : `${(time / 1000).toFixed(2)}s`
  }, [])
  return (
    <div className={`node-content p-4 flex flex-col h-full w-full ${styles.nodeContent}`}>
      {/* Scrollable content area */}
      <div className={`flex-1 overflow-y-auto ${styles.scrollableArea}`}>
        {/* Command Selector */}
        <div className="command-selector mb-4">
          <Label
            htmlFor="command-selector"
            className="block text-sm font-medium mb-1"
          >
            Operation
          </Label>
          <Select
            value={command}
            onValueChange={onCommandChange}
            disabled={isInProgress || isLocked}
          >
            <SelectTrigger id="command-selector" className="w-full">
              <SelectValue placeholder="Select operation" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Generate">Generate</SelectItem>
              <SelectItem value="Enhance">Enhance</SelectItem>
              <SelectItem value="Debug">Debug</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Code Editor */}
        <div className="code-editor mb-4">
          <Label
            htmlFor="code-editor"
            className="block text-sm font-medium mb-1"
          >
            JavaScript Code
          </Label>          <NodeTextarea
            value={codeInput}
            onChange={onCodeChange}
            onBlur={onCodeBlur}
            disabled={isInProgress || isLocked}
            className="min-h-[120px] font-mono"
            placeholder="Enter your JavaScript code here or let the AI generate it based on your requirements..."
            showCopy={!!codeInput.trim()}
            autoResize={true}
          />
        </div>

        {/* Requirements */}
        <div className="requirements mb-4">
          <Label
            htmlFor="requirements-editor"
            className="block text-sm font-medium mb-1"
          >
            Requirements
          </Label>          <NodeTextarea
            value={requirementsInput}
            onChange={onRequirementsChange}
            onBlur={onRequirementsBlur}
            disabled={isInProgress || isLocked}
            className="min-h-[80px]"
            placeholder="Describe what you want the JavaScript code to accomplish..."
            showCopy={!!requirementsInput.trim()}
            autoResize={true}
          />
        </div>

        {/* Context */}
        <div className="context mb-4">
          <Label
            htmlFor="context-editor"
            className="block text-sm font-medium mb-1"
          >
            Context (optional)
          </Label>          <NodeTextarea
            value={contextInput}
            onChange={onContextChange}
            onBlur={onContextBlur}
            disabled={isInProgress || isLocked}
            className="min-h-[60px]"
            placeholder="Add any relevant context that might help with code generation or execution..."
            showCopy={!!contextInput.trim()}
            autoResize={true}
          />
        </div>

        {/* Linked Knowledge Display */}
        {linkedKnowledge && dataSettings.inputLinks && dataSettings.inputLinks.length > 0 && (
          <div className="mt-4 p-2 border rounded-md bg-gray-50">
            <h3 className="font-medium mb-2">Linked Knowledge:</h3>            <CopyableContent content={linkedKnowledge || ''}>
              <div className="text-sm whitespace-pre-wrap">
                {linkedKnowledge}
              </div>
            </CopyableContent>
          </div>
        )}

        {/* Debug Info */}
        {showDebugInfo && (
          <div className="mt-4 p-2 border rounded-md bg-gray-100 text-xs font-mono">
            <h4 className="font-bold mb-1">Debug Info:</h4>
            <div>Node ID: {nodeId}</div>
            <div>Processing: {isProcessing ? 'Yes' : 'No'}</div>
            <div>Has Result: {hasResult ? 'Yes' : 'No'}</div>
            <div>Has Error: {hasError ? 'Yes' : 'No'}</div>
            <div>Has Execution Result: {hasExecutionResult ? 'Yes' : 'No'}</div>
            <div>Is In Progress: {isInProgress ? 'Yes' : 'No'}</div>
            <div>Command: {command}</div>
            <div>Input Links: {dataSettings.inputLinks?.length || 0}</div>
            <div>Auto Sync: {dataSettings.autoSync ? 'Yes' : 'No'}</div>
          </div>
        )}
      </div>      {/* Fixed bottom area for results, actions, and status */}
      <div className={`sticky bottom-0 left-0 right-0 bg-white pt-2 pb-1 ${styles.stickyBottom}`}>
        {/* Results */}
        {hasResult && (
          <div className="result mb-4 p-3 bg-green-50 border border-green-200 rounded-md">
            <Label className="block text-sm font-medium mb-1">
              Generated JavaScript:
            </Label>            <CopyableContent content={data.javascriptCommand?.result || ''}>
              <pre className="bg-gray-800 text-gray-100 p-4 rounded-md overflow-auto max-h-[200px] font-mono text-sm mb-4">
                {data.javascriptCommand?.result}
              </pre>
            </CopyableContent>
            <div className="action-buttons flex gap-2">
              <Button onClick={onAcceptResult} variant="default">
                Accept
              </Button>
              <Button onClick={onResetProcessing} variant="outline">
                Reject
              </Button>
            </div>
          </div>
        )}

        {/* Execution Results */}
        {hasExecutionResult && (
          <div className="result-execution mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
            <Label className="block text-sm font-medium mb-1">
              Execution Result:
            </Label>

            {data.javascriptCommand?.executionResult?.success ? (
              <div className="text-green-600 font-medium mb-2">
                Execution successful (
                {formatExecutionTime(
                  data.javascriptCommand.executionResult.executionTime
                )}
                )
              </div>
            ) : (
              <div className="text-red-600 font-medium mb-2">
                Execution failed (
                {formatExecutionTime(
                  data.javascriptCommand.executionResult.executionTime
                )}
                )
              </div>
            )}

            {/* Output section */}
            {data.javascriptCommand?.executionResult?.output !== undefined &&
              data.javascriptCommand.executionResult.output !== null && (
                <div className="mb-3">
                  <Label className="block text-sm font-medium mb-1">
                    Output:
                  </Label>                  <CopyableContent content={typeof data.javascriptCommand.executionResult.output === 'object'
                    ? JSON.stringify(data.javascriptCommand.executionResult.output, null, 2)
                    : String(data.javascriptCommand.executionResult.output)}>
                    <pre className="bg-gray-800 text-gray-100 p-4 rounded-md overflow-auto max-h-[120px] font-mono text-sm">
                      {typeof data.javascriptCommand.executionResult.output === 'object'
                        ? JSON.stringify(
                            data.javascriptCommand.executionResult.output,
                            null,
                            2
                          )
                        : String(data.javascriptCommand.executionResult.output)}
                    </pre>
                  </CopyableContent>
                </div>
              )}

            {/* Console output section */}
            {data.javascriptCommand?.executionResult?.console &&
              data.javascriptCommand.executionResult.console.length > 0 && (
                <div className="mb-3">
                  <Label className="block text-sm font-medium mb-1">
                    Console output:
                  </Label>                  <CopyableContent content={data.javascriptCommand.executionResult.console.join('\n')}>
                    <pre className="bg-gray-800 text-gray-100 p-4 rounded-md overflow-auto max-h-[120px] font-mono text-sm">
                      {data.javascriptCommand.executionResult.console.join('\n')}
                    </pre>
                  </CopyableContent>
                </div>
              )}

            {/* Error section */}
            {data.javascriptCommand?.executionResult?.error && (
              <div className="mb-3">
                <Label className="block text-sm font-medium text-red-600 mb-1">
                  Error:
                </Label>                <CopyableContent content={data.javascriptCommand.executionResult.error}>
                  <pre className="bg-red-900 text-gray-100 p-4 rounded-md overflow-auto max-h-[120px] font-mono text-sm">
                    {data.javascriptCommand.executionResult.error}
                  </pre>
                </CopyableContent>
              </div>
            )}

            <div className="action-buttons flex gap-2">
              <Button onClick={onResetProcessing} variant="outline">
                Close
              </Button>
            </div>
          </div>
        )}

        {/* Error Display */}
        {hasError && (
          <div className="error mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
            <Label className="block text-sm font-medium mb-1">
              Error:
            </Label>            <CopyableContent content={data.javascriptCommand?.error || ''}>
              <pre className="bg-gray-800 text-gray-100 p-4 rounded-md overflow-auto max-h-[200px] font-mono text-sm mb-4">
                {data.javascriptCommand?.error}
              </pre>
            </CopyableContent>
            <Button onClick={onResetProcessing} variant="outline">
              Reset
            </Button>
          </div>
        )}

        {/* Action Buttons - Only show when no results */}
        {!hasResult && !hasError && !hasExecutionResult && (
          <div className="action-buttons flex justify-end gap-2">
            <Button
              onClick={onStartProcessing}
              disabled={
                isInProgress ||
                isLocked ||
                (!requirementsInput.trim() && !codeInput.trim())
              }
              className="flex items-center generate-button"
              variant="default"
            >
              {isInProgress ? (
                <>
                  <RefreshCw className="h-4 w-4 animate-spin mr-2" />
                  Processing...
                </>
              ) : (
                <>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  {command} JavaScript
                </>
              )}
            </Button>

            <Button
              onClick={onExecuteJavaScript}
              disabled={isInProgress || isLocked || !codeInput.trim()}
              className="flex items-center run-button"
              variant="secondary"
            >
              {isInProgress && command === 'Execute' ? (
                <>
                  <RefreshCw className="h-4 w-4 animate-spin mr-2" />
                  Executing...
                </>
              ) : (
                <>
                  <Play className="h-4 w-4 mr-2" />
                  Run JavaScript
                </>
              )}
            </Button>
          </div>
        )}

        {/* Status Message */}
        {statusMessage && (          <StatusIndicator
            status={isInProgress ? 'loading' : hasError ? 'error' : 'success'}
            message={statusMessage}
            className="mt-2"
          />
        )}
      </div>
    </div>
  )
}
